import { useEffect, useRef, useState } from "react";
import { parseTimeOfDay } from "../lib/visibleFrom";

interface Props {
  value: string | null;
  onChange: (next: string | null) => void;
}

const HOURS = Array.from({ length: 24 }, (_, h) => String(h).padStart(2, "0"));
const MINUTES = ["00", "15", "30", "45"];

export default function VisibleFromField({ value, onChange }: Props) {
  const [draft, setDraft] = useState(value ?? "");
  const [pickerOpen, setPickerOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDraft(value ?? "");
  }, [value]);

  useEffect(() => {
    if (!pickerOpen) return;
    function onMouseDown(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setPickerOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        setPickerOpen(false);
      }
    }
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [pickerOpen]);

  function commit(typed: string) {
    const cleared = typed.trim() === "";
    const next = cleared ? null : parseTimeOfDay(typed);
    if (!cleared && next === null) {
      setDraft(value ?? "");
      return;
    }
    setDraft(next ?? "");
    if (next !== (value ?? null)) onChange(next);
  }

  const [pickedHour, pickedMinute] = value ? value.split(":") : [null, null];

  function pickHour(hour: string) {
    onChange(`${hour}:${pickedMinute ?? "00"}`);
  }

  function pickMinute(minute: string) {
    onChange(`${pickedHour ?? "00"}:${minute}`);
    setPickerOpen(false);
  }

  return (
    <div className="relative" ref={wrapRef}>
      <label className="details-field" title="До этого времени задача не видна в списках">
        <button
          type="button"
          aria-label="Выбрать время"
          aria-expanded={pickerOpen}
          onClick={(e) => {
            e.preventDefault();
            setPickerOpen((open) => !open);
          }}
          className="flex-shrink-0 cursor-pointer"
        >
          <svg
            className={`w-4 h-4 ${value ? "text-blue-500" : "text-gray-400"}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </button>
        <span>с</span>
        <input
          type="text"
          inputMode="numeric"
          placeholder="чч:мм"
          maxLength={5}
          value={draft}
          aria-label="Показывать с"
          onFocus={() => setPickerOpen(true)}
          onClick={() => setPickerOpen(true)}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              setPickerOpen(false);
              e.currentTarget.blur();
            }
          }}
          className="w-12 min-w-0 bg-transparent text-sm tabular-nums focus:outline-none placeholder-gray-400"
        />
        {value && (
          <button
            type="button"
            aria-label="Показывать всегда"
            onClick={(e) => {
              e.preventDefault();
              onChange(null);
            }}
            className="details-field-clear ml-auto"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </label>
      {pickerOpen && (
        <div className="details-popover left-auto w-60" role="dialog" aria-label="Время показа">
          <div className="grid grid-cols-6 gap-1" role="group" aria-label="Час">
            {HOURS.map((hour) => (
              <TimeCell key={hour} label={hour} selected={hour === pickedHour} onPick={() => pickHour(hour)} />
            ))}
          </div>
          <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-800 grid grid-cols-4 gap-1" role="group" aria-label="Минуты">
            {MINUTES.map((minute) => (
              <TimeCell key={minute} label={`:${minute}`} selected={minute === pickedMinute} onPick={() => pickMinute(minute)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TimeCell({ label, selected, onPick }: { label: string; selected: boolean; onPick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onPick}
      className={`py-1 rounded text-sm tabular-nums cursor-pointer ${
        selected
          ? "bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400"
          : "text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
      }`}
    >
      {label}
    </button>
  );
}
